import React, { forwardRef, Fragment, useEffect, useImperativeHandle, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { makeStyles, Grid } from '@material-ui/core'
import { Form } from 'react-final-form'
import Button from 'components/Button'
import Accordion from 'components/Accordion'
import { FieldText } from 'components/Fields'
import { useSelector, useDispatch } from 'react-redux'
import { getTiposGestao, getIsSavingTiposGestao, getIsUpdatingTiposGestao } from 'store/modules/tiposGestao/tiposGestao.selectors'
import { validateFormValues } from 'utils/forms'
import { useConfirmationDialog } from 'components/Dialog/ConfirmDialog'
import { callCriarTipoGestao, callAtualizarTipoGestao, callExcluirTipoGestao } from 'store/modules/tiposGestao'
import { getTipoUsuarioLogado } from 'store/modules/auth/auth.selectors'
import { TIPO_USUARIO } from 'utils/constantes'

const useStyles = makeStyles((theme) => ({
  rowText: {
    display: 'flex',
    alignItems: 'center',
    height: theme.spacing(5),
    fontSize: theme.typography.pxToRem(14),
  },
  formAdd: {
    transition: '0.20s ease-out',
    maxHeight: 0,
    overflow: 'hidden',
  },
  openFormAdd: {
    marginTop: '-10px',
    paddingTop: 10,
    maxHeight: '1300px',
    transition: '0.25s ease-in',
    marginBottom: '32px',
  },
  normalTop: {
    marginTop: '16px',
  },
  alignRight: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingRight: '0 !important',
  },
  itemAlignRight: {
    maxWidth: '190px',
  },
  listItems: {
    border: '1px solid #C9CEDB',
    boxShadow: '0px 8px 32px rgba(15, 52, 68, 0.08)',
    borderRadius: '8px',
    padding: '0px 16px',
  },
  itemTitle: {
    fontFamily: 'Poppins',
    color: '#0F3444',
    fontWeight: '600',
    fontSize: '16px',
  },
  divider: {
    height: 1,
    width: 'calc(100% + 32px)',
    marginLeft: '-16px',
    backgroundColor: theme.palette.grey.main,
  },
  dividerField: {
    borderBottom: `1px solid ${theme.palette.grey.main}`,
    margin: '8px 0',
  },
  itemsForm: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    justifyContent: 'space-between',
    rowGap: '20px',
  },
  itemFormField: {
    width: 'calc( 50% - 4px )',
  },
  itemFormFull: {
    width: '100%',
  },
}))

const TabEstacoes = forwardRef(({ paisId, controlAdded, setControlAdded }, ref) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const dispatch = useDispatch()
  const isSaving = useSelector(getIsSavingTiposGestao)
  const isUpdating = useSelector(getIsUpdatingTiposGestao)
  const dialog = useConfirmationDialog()
  const listaTipoGestao = useSelector(getTiposGestao)
  const tipoUsuarioLogado = useSelector(getTipoUsuarioLogado)
  const [data, setData] = useState(listaTipoGestao)

  const desabilitarFormulario = Boolean(tipoUsuarioLogado === TIPO_USUARIO.ADMIN.OBSERVADOR)

  const validation = yup.object({
    nome: yup.string().required(),
  })

  const handleUpdate = (values) => {
    const { id } = values
    const payload = ({ ...values, paisId })
    delete payload.id
    dispatch(callAtualizarTipoGestao({ id, tipoGestao: payload }))
  }

  const handleSave = (values, form) => {
    const payload = ({ ...values, paisId })
    dispatch(callCriarTipoGestao(payload))
    form.reset()
    Object.keys(values).forEach((key) => {
      form.resetFieldState(key)
    })
  }

  const handleDelete = (id, tipoGestao) => dialog.showConfirmation({
    title: t('dialogs.confirmarExclusao'),
    message: t('dialogs.excluirTipoGestao', {
      tipoGestao,
    }),
  }).then((result) => {
    if (result) {
      dispatch(callExcluirTipoGestao({ id }))
    }
    return null
  })

  const [openItem, setOpenItem] = useState('')

  const handleExpand = (item) => {
    setOpenItem(openItem === item ? '' : item)
  }

  useImperativeHandle(
    ref,
    () => ({
      handleButtonAdded() {
        setControlAdded(true)
        handleExpand('')
      },
    }),
  )

  const clearFields = (form, values, type) => {
    if (type === 'add') {
      setControlAdded(!controlAdded)
    }
    form.reset()
    Object.keys(values).forEach((key) => {
      form.resetFieldState(key)
    })
  }

  const FieldsForm = () => (
    <div className={classes.itemsForm}>
      <FieldText
        required
        label={t('inputs.TipoGestao')}
        name="nome"
        className={classes.itemFormFull}
        disabled={desabilitarFormulario}
      />
    </div>
  )

  const ItemForm = (position) => {
    const itemEstacao = [data[position]]
    return (
      <Form
        onSubmit={handleUpdate}
        validate={validateFormValues(validation)}
        initialValues={{
          nome: itemEstacao[0]?.nome ?? '',
          id: itemEstacao[0]?.id ?? '',
        }}
        render={({ handleSubmit, form, pristine, valid }) => (
          <form onSubmit={handleSubmit}>
            <Accordion
              hideButton
              variant="no-borders"
              classes={{ title: classes.itemTitle }}
              title={itemEstacao[0]?.nome}
              onDelete={() => {
                handleDelete(
                  itemEstacao[0]?.id,
                  itemEstacao[0]?.nome,
                )
                handleExpand('')
              }}
              expanded={position === openItem}
              onExpand={() => handleExpand(position)}
              disableDelete={desabilitarFormulario}
            >
              <Grid container>
                { FieldsForm() }
                <Grid item xs={12}>
                  <Grid
                    container
                    direction="row"
                    spacing={2}
                    justifyContent="space-between"
                    className={classes.normalTop}
                  >
                    <Grid item className={classes.deleteButton}>
                      <Button
                        variant="danger"
                        onClick={() => { form.reset(); handleExpand('') }}
                      >
                        {t('inputs.cancelar')}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        type="submit"
                        disabled={Boolean(pristine || !valid) || desabilitarFormulario}
                        loading={isUpdating}
                      >
                        {t('inputs.salvar')}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Accordion>
            <div className={classes.divider} />
          </form>
        )}
      />
    )
  }

  useEffect(() => {
    setData(listaTipoGestao)
  }, [listaTipoGestao])

  return (
    <>
      <div
        className={clsx(
          classes.formAdd,
          controlAdded && classes.openFormAdd,
        )}
      >
        <Form
          onSubmit={handleSave}
          initialValues={{
            nome: '',
          }}
          validate={validateFormValues(validation)}
          render={({ handleSubmit, form, values, valid }) => (
            <form onSubmit={handleSubmit}>
              <Grid container>
                { FieldsForm() }
                <Grid item xs={12}>
                  <Grid
                    container
                    direction="row"
                    spacing={2}
                    justifyContent="space-between"
                    className={classes.normalTop}
                  >
                    <Grid item className={classes.deleteButton}>
                      <Button
                        variant="danger"
                        onClick={(e) => {
                          e.preventDefault()
                          clearFields(form, values, 'add')
                        }}
                      >
                        {t('inputs.cancelar')}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        type="submit"
                        disabled={!valid}
                        loading={isSaving}
                      >
                        {t('inputs.salvar')}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          )}
        />
      </div>
      <div className={classes.listItems}>
        {
          data.length > 0 &&
          data.map((elem, index) => (
            <Fragment key={index}>
              {ItemForm(index)}
            </Fragment>
          ))
      }
      </div>
    </>
  )
})

TabEstacoes.propTypes = {
  paisId: PropTypes.number,
  controlAdded: PropTypes.bool,
  setControlAdded: PropTypes.func,
}

export default TabEstacoes
